import { GAME_HOUR_MS, turnOf } from "../time/game-clock.ts";
import { weatherAt, type WeatherTable } from "../weather/weather.ts";
import type { CropDefinition, WaterNeed } from "./crop-types.ts";
import type { FarmingRules } from "./farming.ts";

/**
 * Crop growth (ADR-144 decision 12): a crop is a row of timestamps and counters, and its state at any moment is computed
 * lazily by stepping game hour by game hour from where it was last evaluated. Pure and deterministic: the map server, the
 * gateway sweep, a test and the Crop editor's preview get the same answer from the same inputs, however often and in whatever
 * pieces the crop was evaluated.
 */

/**
 * Where a crop is in its life: growing, wilted from drought, ripe, or dead.
 */
export type CropStatus = "growing" | "wilted" | "ripe" | "dead";

/**
 * The state of one planted crop. Times are milliseconds since the Unix epoch; the counters are game hours.
 *
 * `stage` and `stageHours` say how far the crop has grown (moist hours in the current stage). `moistHours` is the water left
 * before the soil is dry; `dryHours` the unbroken hours without water; `wetHours` and `growHours` the moist hours and all hours
 * since planting or since a regrow, whose ratio is the watering score. `evaluatedAt` moves forward only by whole game hours, so
 * evaluating in pieces gives the state of evaluating at once.
 */
export interface CropState {
  readonly plantedAt: number;
  readonly evaluatedAt: number;
  readonly stage: number;
  readonly stageHours: number;
  readonly moistHours: number;
  readonly dryHours: number;
  readonly wetHours: number;
  readonly growHours: number;
  readonly status: CropStatus;
  readonly ripeAt: number | null;
  readonly diedAt: number | null;
}

/**
 * Whether it rains on the crop's region in a turn.
 */
export type RainAt = (turn: number) => boolean;

/**
 * What else is true while a crop is evaluated: the time until which the owner has hired the farmhand to keep watch, which
 * waters every game hour that starts before it.
 */
export interface EvaluateOptions {
  readonly watchedUntilMs?: number;
}

/**
 * The state of a seed just planted: dry, at the first stage.
 *
 * @param plantedAt The time of planting in milliseconds since the Unix epoch.
 * @returns The new crop's state.
 */
export function plantCrop(plantedAt: number): CropState {
  return { plantedAt, evaluatedAt: plantedAt, stage: 0, stageHours: 0, moistHours: 0, dryHours: 0, wetHours: 0, growHours: 0, status: "growing", ripeAt: null, diedAt: null };
}

/**
 * The game hours one watering or rain lasts at a stage that needs a given water.
 *
 * @param rules The farming rules.
 * @param need The stage's water need.
 * @returns The hours; 0 for a stage that needs none, which never dries.
 */
export function waterHoursFor(rules: FarmingRules, need: WaterNeed): number {
  return need === "none" ? 0 : rules.waterHours[need];
}

/**
 * Binds the shared weather to one region: whether any rain or storm falls in a turn.
 *
 * @param weather The weather table.
 * @param worldSeed The world's seed.
 * @param regionId The crop's region (`weatherRegionOf`).
 * @returns The rain test `evaluateCrop` reads, once per turn.
 */
export function rainAtFor(weather: WeatherTable, worldSeed: number, regionId: string): RainAt {
  return (turn) => weatherAt(weather, worldSeed, regionId, turn).kind !== "clear";
}

/**
 * Waters a crop by hand: fills the water to the current stage's need and clears a wilt.
 *
 * @param def The crop's definition.
 * @param rules The farming rules.
 * @param state The crop, evaluated to the time of watering.
 * @returns The watered crop; a ripe or dead crop is returned as it is.
 */
export function waterCrop(def: CropDefinition, rules: FarmingRules, state: CropState): CropState {
  if (state.status === "ripe" || state.status === "dead") return state;
  return { ...state, moistHours: waterHoursFor(rules, def.stages[state.stage]!.water), dryHours: 0, status: "growing" };
}

/**
 * Brings a crop up to a time.
 *
 * Steps whole game hours from `evaluatedAt`. Each hour reads the weather of the turn it starts in (once per turn), refills the
 * water to the stage's need when it rains or the owner's watch covers it, then either grows (a moist hour, or any hour of a
 * stage that needs no water) or dries. Drought wilts the crop at `drought.wiltAfter` unbroken dry hours and kills it at
 * `dieAfter`. Stepping stops at ripe or dead, so the cost is bounded by the crop's total hours plus `dieAfter`.
 *
 * @param def The crop's definition.
 * @param rules The farming rules.
 * @param state The crop's state.
 * @param rainAt Whether it rains in a turn (`rainAtFor`).
 * @param nowMs The time to evaluate to: the caller's server clock, never read here.
 * @param options The owner's watch, when hired.
 * @returns The crop at the last whole game hour at or before `nowMs`.
 * @example
 * evaluateCrop(def, rules, state, rainAtFor(table, seed, region), nowMs, {});
 */
export function evaluateCrop(def: CropDefinition, rules: FarmingRules, state: CropState, rainAt: RainAt, nowMs: number, options: EvaluateOptions): CropState {
  let { evaluatedAt, stage, stageHours, moistHours, dryHours, wetHours, growHours, status, ripeAt, diedAt } = state;
  const watchedUntil = options.watchedUntilMs ?? Number.NEGATIVE_INFINITY;
  let readTurn = -1;
  let raining = false;
  while ((status === "growing" || status === "wilted") && evaluatedAt + GAME_HOUR_MS <= nowMs) {
    const turn = turnOf(evaluatedAt);
    if (turn !== readTurn) {
      readTurn = turn;
      raining = rainAt(turn);
    }
    const current = def.stages[stage]!;
    const hourEnd = evaluatedAt + GAME_HOUR_MS;
    if (current.water !== "none" && (raining || evaluatedAt < watchedUntil)) moistHours = waterHoursFor(rules, current.water);
    growHours += 1;
    if (current.water === "none" || moistHours > 0) {
      if (current.water !== "none") moistHours -= 1;
      dryHours = 0;
      wetHours += 1;
      stageHours += 1;
      status = "growing";
      if (stageHours >= current.hours) {
        stage += 1;
        stageHours = 0;
        const next = def.stages[stage]!.water;
        moistHours = next === "none" ? 0 : Math.min(moistHours, waterHoursFor(rules, next));
        if (stage === def.harvestAt) {
          status = "ripe";
          ripeAt = hourEnd;
        }
      }
    } else {
      dryHours += 1;
      if (dryHours >= def.drought.dieAfter) {
        status = "dead";
        diedAt = hourEnd;
      } else if (dryHours >= def.drought.wiltAfter) {
        status = "wilted";
      }
    }
    evaluatedAt = hourEnd;
  }
  return { plantedAt: state.plantedAt, evaluatedAt, stage, stageHours, moistHours, dryHours, wetHours, growHours, status, ripeAt, diedAt };
}

/**
 * When a ripe crop moves to its owner's farm store: at once when the owner's watch covered the moment it ripened, else after
 * the hold the farmer is given to harvest it by hand.
 *
 * @param state The crop.
 * @param rules The farming rules.
 * @param watchedUntilMs The time the owner's watch ends, or undefined.
 * @returns The time in milliseconds since the Unix epoch, or null when the crop is not ripe.
 */
export function storeDueAt(state: CropState, rules: FarmingRules, watchedUntilMs: number | undefined): number | null {
  if (state.status !== "ripe" || state.ripeAt === null) return null;
  return watchedUntilMs !== undefined && state.ripeAt < watchedUntilMs ? state.ripeAt : state.ripeAt + rules.ripeHoldMs;
}

/**
 * When a dead crop's cell is free again: it shows withered for `deadShownHours` first.
 *
 * @param state The crop.
 * @param rules The farming rules.
 * @returns The time in milliseconds since the Unix epoch, or null when the crop is not dead.
 */
export function cropClearAt(state: CropState, rules: FarmingRules): number | null {
  return state.status === "dead" && state.diedAt !== null ? state.diedAt + rules.deadShownHours * GAME_HOUR_MS : null;
}
